
import { useEffect, useState, useRef } from 'react';

export default function TheMath() {
  const [animated, setAnimated] = useState(false);
  const [countUp, setCountUp] = useState({ founder: 0, preLaunch: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (animated) {
      const duration = 2000;
      const steps = 60;
      const founderTarget = 1488;
      const preLaunchTarget = 840;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        setCountUp({
          founder: Math.floor(founderTarget * progress),
          preLaunch: Math.floor(preLaunchTarget * progress),
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCountUp({ founder: founderTarget, preLaunch: preLaunchTarget });
        }
      }, stepTime);

      return () => clearInterval(interval);
    }
  }, [animated]);

  const tiers = [
    {
      when: 'Founder (now)',
      monthly: '$25',
      annual: '$300',
      savings: countUp.founder,
      discount: '83% off',
      highlight: true,
    },
    {
      when: 'Pre-Launch',
      monthly: '$79',
      annual: '$948',
      savings: countUp.preLaunch,
      discount: '47% off',
      highlight: false,
    },
    {
      when: 'Public Launch',
      monthly: '$149',
      annual: '$1,788',
      savings: 0,
      discount: '$0',
      highlight: false,
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 lg:py-32 bg-[#09090b]">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2
          className={`text-white text-[24px] sm:text-[32px] lg:text-[44px] font-black text-center mb-8 sm:mb-12 lg:mb-16 transition-all duration-700 ${
            animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          The Math
        </h2>

        {/* Mobile Card Layout */}
        <div className="md:hidden space-y-4">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`p-4 rounded-xl transition-all duration-700 ${
                tier.highlight
                  ? 'bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 border-2 border-[#d4af37]'
                  : 'bg-[#0f0f11] border border-[#1a1a1c]'
              } ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className={`text-[16px] font-bold mb-3 ${tier.highlight ? 'text-[#d4af37]' : 'text-white'}`}>
                {tier.when}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[#a1a1aa] text-[10px] mb-1">Monthly</div>
                  <div className="text-white text-[14px] font-semibold">{tier.monthly}</div>
                </div>
                <div>
                  <div className="text-[#a1a1aa] text-[10px] mb-1">Annual</div>
                  <div className="text-white text-[14px] font-semibold">{tier.annual}</div>
                </div>
                <div>
                  <div className="text-[#a1a1aa] text-[10px] mb-1">Savings</div>
                  <div className={`text-[14px] font-bold ${tier.highlight ? 'text-[#d4af37]' : 'text-[#06b6d4]'}`}>
                    {tier.savings > 0 ? `$${tier.savings.toLocaleString()}` : tier.discount}
                    {tier.savings > 0 && <span className="block text-[9px]">({tier.discount})</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden md:block">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-[#a1a1aa] text-sm lg:text-base font-bold px-4 py-3">When You Join</div>
            <div className="text-[#a1a1aa] text-sm lg:text-base font-bold px-4 py-3">Monthly</div>
            <div className="text-[#a1a1aa] text-sm lg:text-base font-bold px-4 py-3">Annual</div>
            <div className="text-[#a1a1aa] text-sm lg:text-base font-bold px-4 py-3">Savings vs Standard</div>
          </div>

          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`grid grid-cols-4 gap-4 mb-4 transition-all duration-700 ${
                tier.highlight
                  ? 'bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 border-2 border-[#d4af37] rounded-xl'
                  : 'bg-[#0f0f11] border border-[#1a1a1c] rounded-xl'
              } ${animated ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className={`px-4 py-4 ${tier.highlight ? 'text-[#d4af37]' : 'text-white'} text-sm lg:text-base font-bold`}>
                {tier.when}
              </div>
              <div className="text-white px-4 py-4 text-sm lg:text-base font-semibold">{tier.monthly}</div>
              <div className="text-white px-4 py-4 text-sm lg:text-base font-semibold">{tier.annual}</div>
              <div className={`px-4 py-4 ${tier.highlight ? 'text-[#d4af37]' : 'text-[#06b6d4]'} text-sm lg:text-base font-bold`}>
                {tier.savings > 0 ? `$${tier.savings.toLocaleString()}/year` : tier.discount}
                {tier.savings > 0 && <span className="block text-xs mt-1">({tier.discount})</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
